import React, { createContext, useContext, useEffect, useState } from "react";
import { api } from "./apiClient";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [checking, setChecking] = useState(true);
  const [ipBlocked, setIpBlocked] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem("qb_token");
    if (!token) {
      setUser(false);
      setChecking(false);
      return;
    }
    // Hard 10s deadline on the /auth/me call — if the endpoint hangs (Cloudflare
    // pending forever, backend crash, etc.) we would otherwise be stuck on
    // "Loading…" indefinitely. Falling back to the login screen is far better
    // UX and lets the user re-authenticate.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 10000);
    api
      .get("/auth/me", { signal: controller.signal })
      .then((r) => setUser(r.data))
      .catch((ex) => {
        if (ex?.response?.status === 403) {
          setIpBlocked(ex.response.data?.detail || "Access denied for your IP address.");
        }
        // Only clear the token on authoritative failures (401/403). Network /
        // timeout errors keep the token so the next reload can retry.
        const s = ex?.response?.status;
        if (s === 401 || s === 403) {
          localStorage.removeItem("qb_token");
        }
        setUser(false);
      })
      .finally(() => { clearTimeout(timer); setChecking(false); });
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("qb_token", data.token);
    // Defence-in-depth: even though the login response now includes the
    // permission flags, immediately fetch /auth/me so the canonical
    // serialiser is the only source of truth for the cached user shape.
    // Prevents future drift between the two endpoints from re-introducing
    // the silent-bypass bug. Falls back to the login response if /auth/me
    // fails for any reason (network blip, etc.).
    try {
      const me = await api.get("/auth/me");
      setUser({ ...data, ...me.data });
    } catch (_) {
      setUser(data);
    }
    return data;
  };

  const register = async (email, password, name, branch) => {
    const { data } = await api.post("/auth/register", { email, password, name, branch });
    return data; // pending — no token
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (_) {}
    localStorage.removeItem("qb_token");
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, checking, login, register, logout, ipBlocked, setIpBlocked }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
